import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import DataTable from "../interfaccia/DataTableBase";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";

const REQUIRED_COLUMNS = [
  "descrizione1",
  "codice esterno",
  "taglia",
  "qty",
  "tab valuta",
  "importo",
  "tab causale",
  "data bolla",
  "data inserimento",
  "nota",
  "ragione sociale",
  "iso code nazione",
];

const SALE_CAUSALI = new Set(["V", "VBW"]);
const REFUND_CAUSALI = new Set(["EV", "EVBW"]);

function parseCsvLine(line, separator) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === separator && !inQuotes) {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  cells.push(current);
  return cells;
}

function detectSeparator(headerLine) {
  const semicolonCount = (headerLine.match(/;/g) || []).length;
  const commaCount = (headerLine.match(/,/g) || []).length;
  return semicolonCount >= commaCount ? ";" : ",";
}

function parseNumber(value) {
  const raw = String(value || "")
    .trim()
    .replace(/\s/g, "");
  const hasComma = raw.includes(",");
  const normalized = hasComma ? raw.replace(/\./g, "").replace(",", ".") : raw;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function normalizeDate(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const italianMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (italianMatch) {
    return [
      italianMatch[3],
      italianMatch[2].padStart(2, "0"),
      italianMatch[1].padStart(2, "0"),
    ].join("-");
  }

  const dashMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (dashMatch) {
    return [
      dashMatch[3],
      dashMatch[2].padStart(2, "0"),
      dashMatch[1].padStart(2, "0"),
    ].join("-");
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function formatImporto(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return value;
  }
  return number.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatData(value) {
  if (!value) {
    return "";
  }
  return String(value).slice(0, 10);
}

async function calcolaHashFile(file) {
  if (!window.crypto || !window.crypto.subtle) {
    return null;
  }
  const buffer = await file.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", buffer);
  const bytes = Array.from(new Uint8Array(hashBuffer));
  return bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function getPeriodoDaRows(rows) {
  const dateValues = rows
    .map((row) => row.transactionCompleteDate)
    .filter(Boolean)
    .sort();

  if (dateValues.length < 1) {
    return { periodoDa: "", periodoA: "" };
  }

  return {
    periodoDa: dateValues[0],
    periodoA: dateValues[dateValues.length - 1],
  };
}

function parseMaybeJson(value, fallback) {
  if (!value) {
    return fallback;
  }
  if (typeof value === "object") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function getTransactionType(causale) {
  const normalized = String(causale || "")
    .trim()
    .toUpperCase();
  if (SALE_CAUSALI.has(normalized)) {
    return "SALE";
  }
  if (REFUND_CAUSALI.has(normalized)) {
    return "REFUND";
  }
  return "";
}

function getStatoControllo(row) {
  if (!row.transactionCompleteDate) {
    return "KO";
  }
  if (row.amount === null || row.amount === undefined) {
    return "KO";
  }
  if (!row.countryCode) {
    return "KO";
  }
  if (String(row.currency || "").toUpperCase() !== "EUR") {
    return "MANUALE";
  }
  return "OK";
}

function getProblemi(row) {
  const problemi = [];
  if (!row.transactionCompleteDate) {
    problemi.push("data bolla non leggibile");
  }
  if (row.amount === null || row.amount === undefined) {
    problemi.push("importo non leggibile");
  }
  if (!row.countryCode) {
    problemi.push("iso code nazione mancante");
  }
  if (String(row.currency || "").toUpperCase() !== "EUR") {
    problemi.push("valuta diversa da EUR");
  }
  return problemi;
}

function normalizeGestionaleRow(rawRow, sourceIndex, csvRowNumber) {
  const causale = String(rawRow["tab causale"] || "")
    .trim()
    .toUpperCase();
  const transactionType = getTransactionType(causale);
  const amount = parseNumber(rawRow.importo);
  const qty = parseNumber(rawRow.qty);
  const transactionCompleteDate = normalizeDate(rawRow["data bolla"]);
  const transactionInsertDate = normalizeDate(rawRow["data inserimento"]);
  const countryCode = String(rawRow["iso code nazione"] || "")
    .trim()
    .toUpperCase();
  const note = String(rawRow.nota || "").trim();
  const externalCode = String(rawRow["codice esterno"] || "").trim();

  const row = {
    ...rawRow,
    sourceIndex,
    csvRowNumber,
    transactionEventId:
      note || `GEST-${csvRowNumber}-${causale || "MOV"}-${externalCode}`,
    transactionType,
    movementCause: causale,
    description: rawRow.descrizione1,
    externalCode,
    size: rawRow.taglia,
    qty: qty === null ? null : Math.abs(qty),
    amount: amount === null ? null : Math.abs(amount),
    currency: String(rawRow["tab valuta"] || "")
      .trim()
      .toUpperCase(),
    transactionCompleteDate,
    transactionDepartDate: transactionCompleteDate,
    transactionInsertDate,
    note,
    customerName: rawRow["ragione sociale"],
    country: countryCode,
    countryCode,
    Paese: countryCode,
  };

  return {
    ...row,
    statoControllo: getStatoControllo(row),
    problemi: getProblemi(row),
    controlliManuali:
      String(row.currency || "").toUpperCase() !== "EUR"
        ? ["valuta da verificare"]
        : [],
  };
}

function parseGestionaleCsv(text) {
  const normalizedText = text
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  const lines = normalizedText.split("\n").filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    throw new Error("Il CSV non contiene righe dati");
  }

  const separator = detectSeparator(lines[0]);
  const headers = parseCsvLine(lines[0], separator).map((header) =>
    header.trim(),
  );
  const missingColumns = REQUIRED_COLUMNS.filter(
    (column) => !headers.includes(column),
  );
  if (missingColumns.length > 0) {
    throw new Error(`Colonne mancanti: ${missingColumns.join(", ")}`);
  }

  const ignoredRows = [];
  const rows = [];

  lines.slice(1).forEach((line, index) => {
    const cells = parseCsvLine(line, separator);
    const rawRow = headers.reduce((acc, header, cellIndex) => {
      acc[header] = cells[cellIndex] || "";
      return acc;
    }, {});
    const causale = String(rawRow["tab causale"] || "")
      .trim()
      .toUpperCase();
    if (!getTransactionType(causale)) {
      ignoredRows.push({
        csvRowNumber: index + 2,
        causale,
        descrizione: rawRow.descrizione1,
        importo: rawRow.importo,
      });
      return;
    }

    rows.push(normalizeGestionaleRow(rawRow, rows.length, index + 2));
  });

  if (rows.length < 1) {
    throw new Error("Nessuna riga con causale gestita trovata nel CSV");
  }

  return { rows, ignoredRows };
}

function buildSummary(rows, ignoredRows) {
  return {
    totaleRighe: rows.length,
    righeCsvOrigine: rows.length + ignoredRows.length,
    righeOk: rows.filter((row) => row.statoControllo === "OK").length,
    righeManuali: rows.filter((row) => row.statoControllo === "MANUALE").length,
    righeKo: rows.filter((row) => row.statoControllo === "KO").length,
    sale: rows.filter((row) => row.transactionType === "SALE").length,
    refund: rows.filter((row) => row.transactionType === "REFUND").length,
    ignorate: ignoredRows.length,
  };
}

function StatoBadge({ row }) {
  if (row.statoControllo === "MANUALE") {
    return (
      <div className="tastoArancio ba br3 w2 h2 flex items-center justify-center">
        <span className="material-symbols-outlined">visibility</span>
      </div>
    );
  }

  if (row.statoControllo === "OK") {
    return (
      <div className="tastoVerde ba br3 w2 h2 flex items-center justify-center white">
        <span className="material-symbols-outlined">check</span>
      </div>
    );
  }

  return (
    <div className="tastoRosso ba br3 w2 h2 flex items-center justify-center white">
      <span className="material-symbols-outlined">dangerous</span>
    </div>
  );
}

function TipoTransazione({ row }) {
  if (row.transactionType === "REFUND") {
    return <span className="red">RIMBORSO</span>;
  }

  return <span className="green">VENDITA</span>;
}

function ProblemiCell({ row }) {
  const problemi = Array.isArray(row.problemi) ? row.problemi : [];
  const controlliManuali = Array.isArray(row.controlliManuali)
    ? row.controlliManuali
    : [];

  if (problemi.length < 1 && controlliManuali.length < 1) {
    return <span className="tastoVerde white br2 ph2 pv1">coerente</span>;
  }

  return (
    <div className="flex flex-column">
      {controlliManuali.map((controllo) => (
        <span key={controllo} className="tastoArancio br2 ph2 pv1 mb1">
          {controllo}
        </span>
      ))}
      {problemi.map((problema) => (
        <span key={problema} className="tastoArancio br2 ph2 pv1 mb1">
          {problema}
        </span>
      ))}
    </div>
  );
}

function getStatoRegistrazione(row) {
  return (
    row.statoRegistrazione ||
    row.stato_registrazione ||
    (row.confermata ? "CONFERMATO" : "IN_ATTESA_CONFERMA")
  );
}

function StatoRegistrazioneBadge({ row }) {
  const stato = getStatoRegistrazione(row);
  if (stato === "CONFERMATO") return <div className="tastoVerde white br3 w2 h2 flex items-center justify-center" title="Confermato"><span className="material-symbols-outlined">check</span></div>;
  if (stato === "DA_IGNORARE") return <div className="sfondoColScuro white br3 w2 h2 flex items-center justify-center" title="Ignorato"><span className="material-symbols-outlined">block</span></div>;
  return null;
}

function isRigaSelezionabile(row) {
  return Boolean(row.idRigaControllo);
}

function isRigaConfermabile(row) {
  return (
    isRigaSelezionabile(row) &&
    getStatoRegistrazione(row) === "IN_ATTESA_CONFERMA"
  );
}

function ConfermaCell({ row, isLoading, onConferma, onDeconferma, onIgnora }) {
  if (!row.idRigaControllo) {
    return <span className="colScrittaDef-20">salva file</span>;
  }

  const stato = getStatoRegistrazione(row);
  return (
    <div className="flex items-center">
      {/* <StatoRegistrazioneBadge row={row} /> */}
      <div className="flex items-center ml2">
        {stato === "IN_ATTESA_CONFERMA" ? (
          <>
            <button
              className="tastoVerde white br2 ph2 pv1 mr1 pointer"
              type="button"
              title="Conferma"
              aria-label="Conferma"
              disabled={isLoading}
              onClick={() => onConferma(row)}
            >
              <span className="material-symbols-outlined">thumb_up</span>
            </button>
            <button
              className="sfondoColScuro white br2 ph2 pv1 pointer"
              type="button"
              title="Ignora"
              aria-label="Ignora"
              disabled={isLoading}
              onClick={() => onIgnora(row)}
            >
              <span className="material-symbols-outlined">thumb_down</span>
            </button>
          </>
        ) : (
          <button
            className="tastoRosso white br2 ph2 pv1 pointer"
            type="button"
            title="Riporta in attesa"
            aria-label="Riporta in attesa"
            disabled={isLoading}
            onClick={() => onDeconferma(row)}
          >
            <span className="material-symbols-outlined">undo</span>
          </button>
        )}
      </div>
    </div>
  );
}

function DettaglioRigaCell({ row }) {
  return (
    <div className="flex flex-column">
      <div>
        <span className="b">#{row.csvRowNumber}</span> {row.description}
      </div>
      <div className="f7">
        {row.externalCode} {row.size} - qty {formatImporto(row.qty)}
      </div>
      <div className="f7">
        {row.customerName} {row.note && "- " + row.note}
      </div>
    </div>
  );
}

function ControlloCorrispettiviGestionale({ token }) {
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get("fileId");
  const [fileName, setFileName] = useState("");
  const [fileHash, setFileHash] = useState(null);
  const [periodoDa, setPeriodoDa] = useState("");
  const [periodoA, setPeriodoA] = useState("");
  const [rows, setRows] = useState([]);
  const [ignoredRows, setIgnoredRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState("");
  const [savedFileId, setSavedFileId] = useState(null);
  const [savingFile, setSavingFile] = useState(false);
  const [salvataggioErrore, setSalvataggioErrore] = useState("");
  const [confermaErrore, setConfermaErrore] = useState("");
  const [confermaMessaggio, setConfermaMessaggio] = useState("");
  const [righeInConferma, setRigheInConferma] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkUnconfirming, setBulkUnconfirming] = useState(false);
  const [bulkIgnoring, setBulkIgnoring] = useState(false);
  const [toggleClearSelectedRows, setToggleClearSelectedRows] = useState(false);
  const [filtroTesto, setFiltroTesto] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEsito, setFiltroEsito] = useState("");
  const [filtroConferma, setFiltroConferma] = useState("");

  const resetFiltri = () => {
    setFiltroTesto("");
    setFiltroTipo("");
    setFiltroEsito("");
    setFiltroConferma("");
  };

  const confermaRigaControllo = useCallback(
    async (row) => {
      if (!token || !row.idRigaControllo) {
        return;
      }

      setConfermaErrore("");
      setConfermaMessaggio("");
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));

      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/conferma",
          { abbinamenti: [] },
          { headers: { Authorization: "Bearer " + token } },
        );

        setRows((previousRows) =>
          previousRows.map((currentRow) =>
            currentRow.idRigaControllo === row.idRigaControllo
              ? {
                  ...currentRow,
                  confermata: true,
                  statoRegistrazione: "CONFERMATO",
                  numeroAbbinamenti: 0,
                }
              : currentRow,
          ),
        );
        setSelectedRows((previousRows) =>
          previousRows.filter(
            (selectedRow) =>
              selectedRow.idRigaControllo !== row.idRigaControllo,
          ),
        );
        setToggleClearSelectedRows((previous) => !previous);
        setConfermaMessaggio("Riga confermata");
      } catch (error) {
        setConfermaErrore(
          error.response?.data?.error ||
            error.message ||
            "Errore conferma riga controllo",
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    },
    [token],
  );

  const deconfermaRigaControllo = useCallback(
    async (row, statoRegistrazione = "IN_ATTESA_CONFERMA") => {
      if (!token || !row.idRigaControllo) {
        return;
      }

      setConfermaErrore("");
      setConfermaMessaggio("");
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));

      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/deconferma",
          { statoRegistrazione },
          { headers: { Authorization: "Bearer " + token } },
        );
        setRows((previousRows) =>
          previousRows.map((currentRow) =>
            currentRow.idRigaControllo === row.idRigaControllo
              ? {
                  ...currentRow,
                  confermata: false,
                  statoRegistrazione,
                  numeroAbbinamenti: 0,
                }
              : currentRow,
          ),
        );
        setSelectedRows((previousRows) =>
          previousRows.filter(
            (selectedRow) =>
              selectedRow.idRigaControllo !== row.idRigaControllo,
          ),
        );
        setToggleClearSelectedRows((previous) => !previous);
        setConfermaMessaggio(
          statoRegistrazione === "DA_IGNORARE"
            ? "Riga ignorata"
            : "Riga riportata in attesa conferma",
        );
      } catch (error) {
        setConfermaErrore(
          error.response?.data?.error ||
            error.message ||
            "Errore de-conferma riga controllo",
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    },
    [token],
  );

  const selectedConfirmableRows = useMemo(
    () => selectedRows.filter(isRigaConfermabile),
    [selectedRows],
  );

  const selectedConfirmedRows = useMemo(
    () =>
      selectedRows.filter((row) => getStatoRegistrazione(row) === "CONFERMATO"),
    [selectedRows],
  );

  const handleSelectedRowsChange = useCallback((state) => {
    setSelectedRows(state.selectedRows || []);
  }, []);

  const confermaRigheSelezionate = useCallback(async () => {
    if (!token) {
      return;
    }

    const righeDaConfermare = selectedRows.filter(isRigaConfermabile);
    if (righeDaConfermare.length < 1) {
      setConfermaMessaggio("");
      setConfermaErrore("Nessuna riga selezionata confermabile");
      return;
    }

    setBulkConfirming(true);
    setConfermaErrore("");
    setConfermaMessaggio("");

    const confermate = [];
    const errori = [];

    for (const row of righeDaConfermare) {
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));

      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/conferma",
          { abbinamenti: [] },
          { headers: { Authorization: "Bearer " + token } },
        );
        confermate.push(row.idRigaControllo);
      } catch (error) {
        errori.push(
          row.transactionEventId +
            ": " +
            (error.response?.data?.error || error.message || "errore conferma"),
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    }

    if (confermate.length > 0) {
      const confermateSet = new Set(confermate);
      setRows((previousRows) =>
        previousRows.map((currentRow) =>
          confermateSet.has(currentRow.idRigaControllo)
            ? {
                ...currentRow,
                confermata: true,
                statoRegistrazione: "CONFERMATO",
                numeroAbbinamenti: 0,
              }
            : currentRow,
        ),
      );
      setSelectedRows([]);
      setToggleClearSelectedRows((previous) => !previous);
    }

    if (errori.length > 0) {
      setConfermaErrore(
        "Confermate " +
          confermate.length +
          " righe. Errori " +
          errori.length +
          ": " +
          errori.slice(0, 3).join(" | "),
      );
    } else {
      setConfermaMessaggio(
        "Confermate " + confermate.length + " righe selezionate",
      );
    }

    setBulkConfirming(false);
  }, [selectedRows, token]);

  const ignoraRigheSelezionate = useCallback(async () => {
    if (!token) {
      return;
    }

    const righeDaIgnorare = selectedRows.filter(isRigaConfermabile);
    if (righeDaIgnorare.length < 1) {
      setConfermaMessaggio("");
      setConfermaErrore("Nessuna riga in attesa selezionata");
      return;
    }

    setBulkIgnoring(true);
    setConfermaErrore("");
    setConfermaMessaggio("");
    const ignorate = [];
    const errori = [];

    for (const row of righeDaIgnorare) {
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));
      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/deconferma",
          { statoRegistrazione: "DA_IGNORARE" },
          { headers: { Authorization: "Bearer " + token } },
        );
        ignorate.push(row.idRigaControllo);
      } catch (error) {
        errori.push(
          row.transactionEventId +
            ": " +
            (error.response?.data?.error || error.message || "errore ignoramento"),
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    }

    if (ignorate.length > 0) {
      const ignorateSet = new Set(ignorate);
      setRows((previousRows) =>
        previousRows.map((currentRow) =>
          ignorateSet.has(currentRow.idRigaControllo)
            ? {
                ...currentRow,
                confermata: false,
                statoRegistrazione: "DA_IGNORARE",
                numeroAbbinamenti: 0,
              }
            : currentRow,
        ),
      );
      setSelectedRows([]);
      setToggleClearSelectedRows((previous) => !previous);
    }

    if (errori.length > 0) {
      setConfermaErrore(
        "Ignorate " +
          ignorate.length +
          " righe. Errori " +
          errori.length +
          ": " +
          errori.slice(0, 3).join(" | "),
      );
    } else {
      setConfermaMessaggio("Ignorate " + ignorate.length + " righe selezionate");
    }

    setBulkIgnoring(false);
  }, [selectedRows, token]);

  const deconfermaRigheSelezionate = useCallback(async () => {
    if (!token) {
      return;
    }

    const righeDaDeconfermare = selectedRows.filter(
      (row) => getStatoRegistrazione(row) === "CONFERMATO",
    );
    if (righeDaDeconfermare.length < 1) {
      setConfermaMessaggio("");
      setConfermaErrore("Nessuna riga confermata selezionata");
      return;
    }

    setBulkUnconfirming(true);
    setConfermaErrore("");
    setConfermaMessaggio("");
    const deconfermate = [];
    const errori = [];
    const statoRegistrazione = "IN_ATTESA_CONFERMA";

    for (const row of righeDaDeconfermare) {
      setRigheInConferma((previous) => ({
        ...previous,
        [row.idRigaControllo]: true,
      }));
      try {
        await axios.patch(
          apiUrl +
            "/contab/controlli-corrispettivi/righe/" +
            row.idRigaControllo +
            "/deconferma",
          { statoRegistrazione },
          { headers: { Authorization: "Bearer " + token } },
        );
        deconfermate.push(row.idRigaControllo);
      } catch (error) {
        errori.push(
          row.transactionEventId +
            ": " +
            (error.response?.data?.error ||
              error.message ||
              "errore de-conferma"),
        );
      } finally {
        setRigheInConferma((previous) => ({
          ...previous,
          [row.idRigaControllo]: false,
        }));
      }
    }

    if (deconfermate.length > 0) {
      const deconfermateSet = new Set(deconfermate);
      setRows((previousRows) =>
        previousRows.map((currentRow) =>
          deconfermateSet.has(currentRow.idRigaControllo)
            ? {
                ...currentRow,
                confermata: false,
                statoRegistrazione,
                numeroAbbinamenti: 0,
              }
            : currentRow,
        ),
      );
      setSelectedRows([]);
      setToggleClearSelectedRows((previous) => !previous);
    }

    if (errori.length > 0) {
      setConfermaErrore(
        "De-confermate " +
          deconfermate.length +
          " righe. Errori " +
          errori.length +
          ": " +
          errori.slice(0, 3).join(" | "),
      );
    } else {
      setConfermaMessaggio(
        "De-confermate " + deconfermate.length + " righe selezionate",
      );
    }

    setBulkUnconfirming(false);
  }, [selectedRows, token]);

  const columns = useMemo(
    () => [
      {
        name: "Esito",
        width: "60px",
        cell: (row) => <StatoBadge row={row} />,
      },
      {
        name: "Tipo",
        width: "105px",
        selector: (row) => row.transactionType,
        sortable: true,
        cell: (row) => <TipoTransazione row={row} />,
      },
      {
        name: "Causale",
        width: "95px",
        selector: (row) => row.movementCause,
        sortable: true,
      },
      {
        name: "Stato",
        width: "70px",
        cell: (row) => <StatoRegistrazioneBadge row={row} />,
      },
      {
        name: "Registrazione",
        width: "150px",
        cell: (row) => (
          <ConfermaCell
            row={row}
            isLoading={Boolean(righeInConferma[row.idRigaControllo])}
            onConferma={confermaRigaControllo}
            onDeconferma={deconfermaRigaControllo}
            onIgnora={(row) => deconfermaRigaControllo(row, "DA_IGNORARE")}
          />
        ),
      },
      {
        name: "Data bolla",
        width: "125px",
        selector: (row) => row.transactionCompleteDate,
        sortable: true,
        cell: (row) => formatData(row.transactionCompleteDate),
      },
      {
        name: "Data ins.",
        width: "125px",
        selector: (row) => row.transactionInsertDate,
        sortable: true,
        cell: (row) => formatData(row.transactionInsertDate),
      },
      {
        name: "Paese",
        width: "90px",
        selector: (row) => row.countryCode,
        sortable: true,
      },
      {
        name: "Valuta",
        width: "90px",
        selector: (row) => row.currency,
        sortable: true,
      },
      {
        name: "Importo",
        width: "120px",
        selector: (row) => row.amount,
        sortable: true,
        right: true,
        cell: (row) => formatImporto(row.amount),
      },
      {
        name: "Nota",
        width: "180px",
        selector: (row) => row.note,
        sortable: true,
      },
      {
        name: "Esito",
        width: "230px",
        cell: (row) => <ProblemiCell row={row} />,
      },
      {
        name: "Riga gestionale",
        grow: 2,
        wrap: true,
        cell: (row) => <DettaglioRigaCell row={row} />,
      },
    ],
    [confermaRigaControllo, deconfermaRigaControllo, righeInConferma],
  );

  const filteredRows = useMemo(() => {
    const testo = filtroTesto.trim().toLowerCase();

    return rows.filter((row) => {
      const matchTesto =
        !testo ||
        [
          row.transactionEventId,
          row.note,
          row.customerName,
          row.externalCode,
          row.description,
          row.countryCode,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(testo));
      const matchTipo = !filtroTipo || row.transactionType === filtroTipo;
      const matchEsito = !filtroEsito || row.statoControllo === filtroEsito;
      const matchConferma =
        !filtroConferma || getStatoRegistrazione(row) === filtroConferma;

      return matchTesto && matchTipo && matchEsito && matchConferma;
    });
  }, [rows, filtroTesto, filtroTipo, filtroEsito, filtroConferma]);

  useEffect(() => {
    if (!fileId || !token) {
      return;
    }

    setLoading(true);
    setErrore("");
    axios
      .get(apiUrl + "/contab/controlli-corrispettivi/files/" + fileId, {
        headers: { Authorization: "Bearer " + token },
      })
      .then((response) => {
        const file = response.data.file || {};
        const righe = response.data.righe || [];
        const nextRows = righe.map((riga) => {
          const raw = parseMaybeJson(riga.raw_json, {});
          return {
            ...raw,
            sourceIndex: riga.progressivo_riga ?? raw.sourceIndex,
            csvRowNumber: riga.numero_riga || raw.csvRowNumber,
            transactionEventId:
              riga.chiave_transazione || raw.transactionEventId,
            transactionType: riga.tipo_transazione || raw.transactionType,
            amount: riga.importo_report ?? raw.amount,
            currency: riga.valuta || raw.currency,
            transactionCompleteDate:
              formatData(riga.data_transazione) || raw.transactionCompleteDate,
            transactionDepartDate:
              formatData(riga.data_partenza) || raw.transactionDepartDate,
            statoControllo: riga.stato_controllo || raw.statoControllo,
            problemi: parseMaybeJson(riga.problemi_json, raw.problemi || []),
            controlliManuali: parseMaybeJson(
              riga.controlli_manuali_json,
              raw.controlliManuali || [],
            ),
            idRigaControllo: riga.id,
            idFileControllo: riga.id_file,
            statoRegistrazione:
              riga.stato_registrazione ||
              (riga.confermata ? "CONFERMATO" : "IN_ATTESA_CONFERMA"),
            confermata: Boolean(riga.confermata),
            numeroAbbinamenti: Number(riga.numero_abbinamenti || 0),
          };
        });

        setFileName(file.nome_file_originale || "File " + fileId);
        setFileHash(file.hash_file || null);
        setPeriodoDa(formatData(file.periodo_da));
        setPeriodoA(formatData(file.periodo_a));
        setRows(nextRows);
        setIgnoredRows([]);
        setSummary(
          buildSummary(
            nextRows,
            parseMaybeJson(file.metadata_json, {}).ignoredRows || [],
          ),
        );
        setSavedFileId(file.id || fileId);
        setSelectedRows([]);
        setToggleClearSelectedRows((previous) => !previous);
        resetFiltri();
      })
      .catch((error) => {
        setErrore(
          error.response?.data?.error ||
            error.message ||
            "Errore caricamento file controllo",
        );
      })
      .finally(() => setLoading(false));
  }, [fileId, token]);

  const salvaFileControllo = async () => {
    if (!token || !fileName || rows.length < 1) {
      return;
    }

    setSavingFile(true);
    setSalvataggioErrore("");
    try {
      const response = await axios.post(
        apiUrl + "/contab/controlli-corrispettivi/files",
        {
          fileName,
          piattaforma: "GESTIONALE",
          tipoReport: "REPORT_CORRISPETTIVI_GESTIONALE_MOVIMENTI",
          hashFile: fileHash,
          account: "GESTIONALE",
          periodoDa: periodoDa || null,
          periodoA: periodoA || null,
          rows,
          summary,
          metadata: {
            source: "controlloCorrispettiviGestionale",
            ignoredRows,
          },
        },
        { headers: { Authorization: "Bearer " + token } },
      );
      const nextSavedFileId = response.data.id;
      const righeSalvate = response.data.righe || [];
      const righeByProgressivo = new Map(
        righeSalvate.map((riga) => [Number(riga.progressivo_riga), riga]),
      );

      setSavedFileId(nextSavedFileId);
      setRows((previousRows) =>
        previousRows.map((row) => {
          const rigaSalvata = righeByProgressivo.get(Number(row.sourceIndex));
          if (!rigaSalvata) {
            return row;
          }

          return {
            ...row,
            idFileControllo: nextSavedFileId,
            idRigaControllo: rigaSalvata.id,
            statoRegistrazione: "IN_ATTESA_CONFERMA",
            confermata: false,
            numeroAbbinamenti: 0,
          };
        }),
      );
    } catch (error) {
      setSalvataggioErrore(
        error.response?.data?.error ||
          error.message ||
          "Errore salvataggio file controllo",
      );
    } finally {
      setSavingFile(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    setErrore("");
    setSalvataggioErrore("");
    setConfermaErrore("");
    setConfermaMessaggio("");
    setSelectedRows([]);
    setToggleClearSelectedRows((previous) => !previous);
    setSavedFileId(null);
    setFileHash(null);
    setPeriodoDa("");
    setPeriodoA("");
    setLoading(true);
    setRows([]);
    setIgnoredRows([]);
    setSummary(null);
    resetFiltri();
    setFileName(file.name);

    try {
      if (!token) {
        throw new Error("Token non ancora disponibile");
      }
      const hash = await calcolaHashFile(file);
      setFileHash(hash);
      const text = await file.text();
      const parsed = parseGestionaleCsv(text);
      const periodo = getPeriodoDaRows(parsed.rows);
      setPeriodoDa(periodo.periodoDa);
      setPeriodoA(periodo.periodoA);
      setRows(parsed.rows);
      setIgnoredRows(parsed.ignoredRows);
      setSummary(buildSummary(parsed.rows, parsed.ignoredRows));
    } catch (error) {
      setErrore(
        error.response?.data?.error || error.message || "Errore nel controllo",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sfondoBiancoTema pa3">
      <div className="flex flex-wrap items-center justify-between bb bordoColAlt pb3 mb3">
        <div>
          <div className="f4 b">Controllo corrispettivi gestionale</div>
          <div className="f6 colScrittaDef-20">
            Export movimenti da gestionale
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end">
          <Link
            className={stilePulsanti.buttonClassesWhite + " mr2"}
            to="/controlliCorrispettivi"
          >
            Indietro
          </Link>
          <label
            className={stilePulsanti.buttonClassesWhite + " flex items-center"}
          >
            {stilePulsanti.tastoCaricaAspetto}
            <span className="ml2">Carica CSV</span>
            <input
              className="dn"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
            />
          </label>
        </div>
      </div>

      {fileName && (
        <div className="mb3">
          <span className="b">{fileName}</span>
          {summary && (
            <span className="ml3">
              Righe {summary.totaleRighe} - origine {summary.righeCsvOrigine} -
              OK {summary.righeOk} - Manuali {summary.righeManuali} - KO{" "}
              {summary.righeKo} - Vendite {summary.sale} - Rimborsi{" "}
              {summary.refund} - Ignorate {summary.ignorate}
            </span>
          )}
        </div>
      )}

      {ignoredRows.length > 0 && (
        <div className="tastoArancio pa3 br2 mb3">
          Righe ignorate per causale non gestita: {ignoredRows.length}
        </div>
      )}

      {fileName && rows.length > 0 && (
        <div className="formRicercaOrizzontale pa2 flex flex-wrap items-center mb3">
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="date"
              value={periodoDa}
              onChange={(event) => setPeriodoDa(event.target.value)}
              disabled={Boolean(savedFileId)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="date"
              value={periodoA}
              onChange={(event) => setPeriodoA(event.target.value)}
              disabled={Boolean(savedFileId)}
            />
          </div>
        </div>
      )}

      {fileName && rows.length > 0 && (
        <div className="mb3 flex flex-wrap items-center">
          {!savedFileId && (
            <button
              className={stilePulsanti.buttonClassesWhite}
              type="button"
              onClick={salvaFileControllo}
              disabled={savingFile}
            >
              {savingFile ? "Salvo..." : "Salva controllo"}
            </button>
          )}
          {savedFileId && (
            <span className="tastoVerde white br2 ph2 pv1">
              File controllo {savedFileId}
            </span>
          )}
        </div>
      )}

      {salvataggioErrore && (
        <div className="tastoRosso white pa3 br2 mb3">{salvataggioErrore}</div>
      )}

      {confermaErrore && (
        <div className="tastoRosso white pa3 br2 mb3">{confermaErrore}</div>
      )}

      <div className="formRicercaOrizzontale pa2 flex flex-wrap justify-between items-center mb3">
        <div className="flex flex-wrap items-center">
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <input
              className="inputOrizzontale"
              type="text"
              placeholder="Nota, cliente, articolo, paese"
              value={filtroTesto}
              onChange={(event) => setFiltroTesto(event.target.value)}
            />
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroTipo}
              onChange={(event) => setFiltroTipo(event.target.value)}
            >
              <option value="">Tipo: tutti</option>
              <option value="SALE">VENDITA</option>
              <option value="REFUND">RIMBORSO</option>
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroEsito}
              onChange={(event) => setFiltroEsito(event.target.value)}
            >
              <option value="">Esito: tutti</option>
              <option value="OK">OK</option>
              <option value="KO">KO</option>
              <option value="MANUALE">MANUALE</option>
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <select
              className="inputOrizzontale"
              value={filtroConferma}
              onChange={(event) => setFiltroConferma(event.target.value)}
            >
              <option value="">Registrazione: tutte</option>
              <option value="IN_ATTESA_CONFERMA">In attesa conferma</option>
              <option value="CONFERMATO">Confermato</option>
              <option value="DA_IGNORARE">Da ignorare</option>
            </select>
          </div>
          <div className="gruppoInputRicercaOrizzontale mr2 mb2">
            <button
              className="dim tastoInputOrizzontale br3"
              type="button"
              onClick={resetFiltri}
            >
              RESET
            </button>
          </div>
        </div>
        <div className="colScrittaDef-20 mb2">
          Righe {filteredRows.length}/{rows.length}
        </div>
      </div>

      {errore && <div className="tastoRosso white pa3 br2 mb3">{errore}</div>}
      {confermaMessaggio && (
        <div className="tastoVerde white pa3 br2 mb3">{confermaMessaggio}</div>
      )}

      {savedFileId && filteredRows.length > 0 && (
        <div className="formRicercaOrizzontale pa2 flex flex-wrap justify-between items-center mb3">
          <div className="colScrittaDef-20 mb2">
            Selezionate {selectedRows.length} - confermabili{" "}
            {selectedConfirmableRows.length} - confermate{" "}
            {selectedConfirmedRows.length}
          </div>
          <div className="flex flex-wrap justify-end items-center mb2">
            <button
              className="tastoVerde white br2 ph2 pv1 mr2 flex justify-center items-center"
              type="button"
              title="Conferma selezionate"
              onClick={confermaRigheSelezionate}
              disabled={bulkConfirming || selectedConfirmableRows.length < 1}
            >
              <span className="material-symbols-outlined mr1">thumb_up</span>
              {bulkConfirming ? "Confermo..." : "Conferma selezionate"}
            </button>
            <button
              className="sfondoColScuro white br2 ph2 pv1 mr2 flex justify-center items-center"
              type="button"
              title="Ignora selezionate"
              onClick={ignoraRigheSelezionate}
              disabled={bulkIgnoring || selectedConfirmableRows.length < 1}
            >
              <span className="material-symbols-outlined mr1">thumb_down</span>
              {bulkIgnoring ? "Ignoro..." : "Ignora selezionate"}
            </button>
            <button
              className="tastoRosso white br2 ph2 pv1 flex justify-center items-center"
              type="button"
              title="De-conferma selezionate"
              onClick={deconfermaRigheSelezionate}
              disabled={bulkUnconfirming || selectedConfirmedRows.length < 1}
            >
              <span className="material-symbols-outlined mr1">undo</span>
              {bulkUnconfirming ? "De-confermo..." : "De-conferma selezionate"}
            </button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={filteredRows}
        progressPending={loading}
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 100, 200]}
        dense
        highlightOnHover
        selectableRows
        selectableRowsVisibleOnly
        selectableRowDisabled={(row) => !isRigaSelezionabile(row)}
        onSelectedRowsChange={handleSelectedRowsChange}
        clearSelectedRows={toggleClearSelectedRows}
      />
    </div>
  );
}

export default ControlloCorrispettiviGestionale;
